import { inject, Injectable } from '@angular/core';
import {
  IOperatorDropDown,
  IOperatorMapping,
  OperatorDropDownId,
  OperatorMappingId,
} from '@web/dtos';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';

export interface IOperatorDropDownForm {
  id: FormControl<OperatorDropDownId | null>;
  name: FormControl<string>;
}

export interface IOperatorMappingForm {
  id: FormControl<OperatorMappingId | null>;
  name: FormControl<string | null>;
  url: FormControl<string>;
  operatorId: FormControl<string>;
  dropDownOptions: FormArray<FormGroup<IOperatorDropDownForm>>;
  onDemandSortExternalRef: FormControl<string | null>;
  comfortSortExternalRef: FormControl<string | null>;
  publicTransportProductGroup: FormControl<string | null>;
}

@Injectable({
  providedIn: 'root',
})
export class OperatorMappingFactory {
  private readonly formBuilder: FormBuilder = inject(FormBuilder);

  buildForm(
    operatorMappings: IOperatorMapping[]
  ): FormArray<FormGroup<IOperatorMappingForm>> {
    return this.formBuilder.array<FormGroup<IOperatorMappingForm>>(
      operatorMappings.map((operatorMapping: IOperatorMapping) =>
        this.buildOperatorMappingFormGroup(operatorMapping)
      )
    );
  }

  buildOperatorMappingFormGroup(
    operatorMapping?: IOperatorMapping
  ): FormGroup<IOperatorMappingForm> {
    return this.formBuilder.group<IOperatorMappingForm>({
      id: this.formBuilder.control<OperatorMappingId | null>(
        operatorMapping?.id || null
      ),
      name: this.formBuilder.control<string | null>(
        operatorMapping?.name || null
      ),
      url: this.formBuilder.nonNullable.control<string>(
        operatorMapping?.url || '',
        Validators.required
      ),
      operatorId: this.formBuilder.nonNullable.control<string>(
        operatorMapping?.operatorId || ''
      ),
      dropDownOptions: this.formBuilder.array<FormGroup<IOperatorDropDownForm>>(
        operatorMapping?.dropDownOptions.map(
          (dropdownOption: IOperatorDropDown) =>
            this.buildOptionForm(dropdownOption)
        ) || []
      ),
      onDemandSortExternalRef: this.formBuilder.control<string | null>(
        operatorMapping?.onDemandSortExternalRef ?? null
      ),
      comfortSortExternalRef: this.formBuilder.control<string | null>(
        operatorMapping?.comfortSortExternalRef ?? null
      ),
      publicTransportProductGroup: this.formBuilder.control<string | null>(
        operatorMapping?.publicTransportProductGroup ?? null
      ),
    });
  }

  buildOptionForm(
    dropdownOption?: IOperatorDropDown
  ): FormGroup<IOperatorDropDownForm> {
    return this.formBuilder.group<IOperatorDropDownForm>({
      id: this.formBuilder.control<OperatorDropDownId | null>(
        dropdownOption?.id || null
      ),
      name: this.formBuilder.nonNullable.control<string>(
        dropdownOption?.name || '',
        [Validators.required]
      ),
    });
  }

  buildEntity(
    form: FormArray<FormGroup<IOperatorMappingForm>>
  ): IOperatorMapping[] {
    return form.value as IOperatorMapping[];
  }
}
